import React, {useState} from "react";
import dayjs from "dayjs";
import {DateRange} from "react-date-range";
import {fr} from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {Modal} from "./MainApp";
import {CURRENT_YEAR, getSeasonEnd, getSeasonStart} from "../season";

const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:4000/api" : "/api");

const MONTHS = [{name: "Juillet", month: 6}, {name: "Août", month: 7},];

function numRange(n) {
    return [...Array(n).keys()];
}

function getDaysInMonth(year, month) {
    const d = dayjs(`${year}-${String(month + 1).padStart(2, "0")}-01`);
    return numRange(d.daysInMonth()).map((i) => d.add(i, "day"));
}

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function PlanningTab({
                                        members, selectedMember, presences, setRefresh, fetchWithToken, token
                                    }) {
    // Ajout présence
    const [addPresenceOpen, setAddPresenceOpen] = useState(false);
    const [range, setRange] = useState([{
        startDate: getSeasonStart(), endDate: getSeasonStart(), key: "selection"
    }]);

    // Edit présence
    const [editPresence, setEditPresence] = useState(null);
    const [editDateRange, setEditDateRange] = useState([{
        startDate: new Date(), endDate: new Date(), key: "selection"
    }]);
    const [editPresenceOpen, setEditPresenceOpen] = useState(false);

    // Commentaire
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [editCommentValue, setEditCommentValue] = useState("");

    // Modal jour
    const [modal, setModal] = useState({open: false, day: null});

    function presentOn(day) {
        return presences
            .filter(p => dayjs(p.arrival).startOf("day").isSameOrBefore(day, "day") && dayjs(p.departure).endOf("day").isSameOrAfter(day, "day"))
            .map(p => {
                const member = members.find(m => m._id === p.member);
                return member ? member.pseudonym : null;
            })
            .filter(Boolean);
    }

    function isSelectedMemberPresentOn(day) {
        if (!selectedMember) return false;
        return presences.some((p) => p.member === selectedMember._id && dayjs(p.arrival).startOf("day").isSameOrBefore(day, "day") && dayjs(p.departure).endOf("day").isSameOrAfter(day, "day"));
    }

    function leavesToday(day) {
        const thisDay = dayjs(day).startOf("day");
        return presences
            .filter(p => dayjs(p.departure).isSame(thisDay, "day"))
            .map(p => {
                const member = members.find(m => m._id === p.member);
                return member ? member.pseudonym : null;
            })
            .filter(Boolean);
    }

    function arrivesToday(day) {
        const thisDay = dayjs(day).startOf("day");
        return presences
            .filter(p => dayjs(p.arrival).isSame(thisDay, "day"))
            .map(p => {
                const member = members.find(m => m._id === p.member);
                return member ? member.pseudonym : null;
            })
            .filter(Boolean);
    }

    // Ajout d'une présence
    const handleAddPresence = async (e) => {
        e.preventDefault();
        if (!selectedMember || !range[0].startDate || !range[0].endDate) return;

        const newStart = dayjs(range[0].startDate).startOf("day");
        const newEnd = dayjs(range[0].endDate).endOf("day");

        // Vérif chevauchement
        const memberPresences = presences.filter(p => p.member === selectedMember._id);
        const overlap = memberPresences.some(p => {
            const existStart = dayjs(p.arrival).startOf("day");
            const existEnd = dayjs(p.departure).endOf("day");
            return newStart.isSameOrBefore(existEnd) && newEnd.isSameOrAfter(existStart);
        });
        if (overlap) {
            alert("Erreur : Vous ne pouvez pas créer deux séjours au même moment.");
            return;
        }

        // POST
        await fetchWithToken(`${API_URL}/presences`, token, {
            method: "POST", headers: {"Content-Type": "application/json"}, body: JSON.stringify({
                memberId: selectedMember._id,
                arrival: newStart.format('YYYY-MM-DD'),
                departure: newEnd.format('YYYY-MM-DD'),
            }),
        });
        setAddPresenceOpen(false);
        setRange([{
            startDate: getSeasonStart(), endDate: getSeasonStart(), key: "selection"
        }]);
        setRefresh(r => r + 1);
    };

    // Edition
    const handleEditPresence = async (e) => {
        e.preventDefault();
        const newStart = editDateRange[0].startDate;
        const newEnd = editDateRange[0].endDate;
        const memberPresences = presences.filter(p => p.member === selectedMember._id && p._id !== editPresence._id);
        const overlap = memberPresences.some(p => {
            const existStart = dayjs(p.arrival).startOf("day");
            const existEnd = dayjs(p.departure).endOf("day");
            return dayjs(newStart).startOf("day").isSameOrBefore(existEnd) && dayjs(newEnd).endOf("day").isSameOrAfter(existStart);
        });
        if (overlap) {
            alert("Erreur : cette période chevauche une autre présence !");
            return;
        }
        await fetchWithToken(`${API_URL}/presences/${editPresence._id}`, token, {
            method: "PATCH", headers: {"Content-Type": "application/json"}, body: JSON.stringify({
                arrival: dayjs(newStart).format("YYYY-MM-DD"), departure: dayjs(newEnd).format("YYYY-MM-DD"),
            }),
        });
        setEditPresenceOpen(false);
        setEditPresence(null);
        setRefresh(r => r + 1);
    };

    // Commentaire
    const handleComment = async (e) => {
        e.preventDefault();
        await fetchWithToken(`${API_URL}/presences/${editPresence._id}`, token, {
            method: "PATCH",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({comment: editCommentValue}),
        });
        setCommentModalOpen(false);
        setEditPresence(null);
        setEditCommentValue("");
        setRefresh(r => r + 1);
    };

    // Suppression
    const handleDelete = async (presenceId) => {
        if (window.confirm("Supprimer ce séjour ?")) {
            await fetchWithToken(`${API_URL}/presences/${presenceId}`, token, {method: "DELETE"});
            setRefresh(r => r + 1);
        }
    };

    return (<div>
        {/* Ajouter une présence */}
        {selectedMember && (<button
            className="mb-6 bg-blue-700 text-white px-4 py-2 rounded-xl hover:bg-blue-800 transition"
            onClick={() => setAddPresenceOpen(true)}
        >
            Ajouter une présence
        </button>)}

        {selectedMember && (<div className="mb-6">
            <h2 className="text-md font-bold mb-2 text-blue-700">Séjours de {selectedMember.pseudonym}</h2>
            {presences.filter(p => p.member === selectedMember._id).length === 0 ? (
                <div className="text-gray-400 italic">Aucun séjour enregistré</div>) : (
                <ul className="flex flex-col gap-3">
                    {presences
                        .filter(p => p.member === selectedMember._id)
                        .sort((a, b) => a.arrival.localeCompare(b.arrival))
                        .map(presence => (<li key={presence._id} className="flex items-center gap-4">
        <span>
          {dayjs(presence.arrival).format("DD/MM/YYYY")} → {dayjs(presence.departure).format("DD/MM/YYYY")}
            {presence.comment && (<span className="ml-2 text-xs text-gray-500 italic">({presence.comment})</span>)}
        </span>
                            <button
                                className="text-xs bg-yellow-200 hover:bg-yellow-300 text-yellow-800 px-2 py-1 rounded"
                                onClick={() => {
                                    setEditPresence(presence);
                                    setEditDateRange([{
                                        startDate: new Date(presence.arrival),
                                        endDate: new Date(presence.departure),
                                        key: 'selection'
                                    }]);
                                    setEditPresenceOpen(true);
                                }}
                            >
                                Modifier
                            </button>
                            <button
                                className="text-xs bg-blue-200 hover:bg-blue-300 text-blue-800 px-2 py-1 rounded"
                                onClick={() => {
                                    setEditPresence(presence);
                                    setEditCommentValue(presence.comment || "");
                                    setCommentModalOpen(true);
                                }}
                            >
                                Commentaire
                            </button>
                            <button
                                className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded"
                                onClick={async () => handleDelete(presence._id)}
                            >
                                Supprimer
                            </button>
                        </li>))}
                </ul>)}
        </div>)}

        {/* CALENDRIERS Juillet & Août */}
        <div className="flex flex-col gap-8">
            {MONTHS.map(({name, month}) => {
                const year = CURRENT_YEAR;
                const days = getDaysInMonth(year, month);
                return (<div key={month}>
                    <h2 className="text-lg font-semibold mb-2">{name} {year}</h2>
                    <div className="grid grid-cols-7 gap-1">
                        {/* Jours de la semaine */}
                        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d, i) => (
                            <div key={i} className="text-center font-medium py-1">{d}</div>))}
                        {/* Décalage premier jour du mois */}
                        {numRange((days[0].day() + 6) % 7).map(i => (<div key={i}></div>))}
                        {/* Jours du mois */}
                        {days.map(day => {
                            const count = presentOn(day).length;
                            const isSelectedPresent = isSelectedMemberPresentOn(day);
                            return (<button
                                key={day.format("YYYY-MM-DD")}
                                className={classNames("border rounded-lg h-16 flex flex-col justify-between items-center py-1 transition", count > 0 ? "border-blue-400 shadow" : "border-gray-200", isSelectedPresent ? "bg-green-100 hover:bg-green-200 border-green-400" : "bg-white hover:bg-blue-50")}
                                onClick={() => setModal({open: true, day, type: "show"})}
                            >
                          <span className={classNames("font-bold", isSelectedPresent ? "text-green-900" : "")}>
                            {day.format("D")}
                          </span>
                                <span
                                    className={classNames("text-xs px-2 py-0.5 rounded", count === 0 ? "text-gray-400 bg-gray-100" : isSelectedPresent ? "bg-green-200 text-green-900 font-semibold" : "bg-blue-100 text-blue-700 font-semibold")}>
                            {count === 0 ? "0 présent" : `${count} présent${count > 1 ? "s" : ""}`}
                          </span>
                            </button>);
                        })}
                    </div>
                </div>);
            })}
        </div>

        <Modal open={editPresenceOpen} onClose={() => setEditPresenceOpen(false)}>
            <form onSubmit={async (e) => handleEditPresence(e)}>
                <div className="font-bold mb-2">Modifier le séjour</div>
                <DateRange
                    locale={fr}
                    editableDateInputs={true}
                    onChange={item => setEditDateRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={editDateRange}
                    minDate={getSeasonStart()}
                    maxDate={getSeasonEnd()}
                    rangeColors={["#f59e42"]} // orange
                />
                <div className="flex gap-2 mt-4">
                    <button
                        type="submit"
                        className="bg-yellow-500 text-white px-4 py-2 rounded-xl hover:bg-yellow-600"
                    >
                        Sauvegarder
                    </button>
                    <button
                        type="button"
                        className="bg-gray-200 px-4 py-2 rounded-xl"
                        onClick={() => setEditPresenceOpen(false)}
                    >
                        Annuler
                    </button>
                </div>
            </form>
        </Modal>
        {/* Modal : liste des présents */}
        <Modal open={modal.open && modal.type === "show"} onClose={() => setModal({open: false})}>
            {modal.day && (<>
                <div className="text-lg font-bold mb-2">
                    Présents le {modal.day.format("dddd D MMMM YYYY")}
                </div>
                <ul>
                    {presentOn(modal.day).length === 0 ? (<li className="text-gray-400 italic">Personne
                        présente</li>) : (presentOn(modal.day).map(name => (
                        <li key={name} className="mb-1">{name}</li>)))}
                </ul>
                <div className="mt-4">
                    <div className="font-semibold mb-1">Partent ce jour :</div>
                    <ul>
                        {leavesToday(modal.day).length === 0 ? (
                            <li className="text-gray-400 italic">Personne</li>) : (leavesToday(modal.day).map(name => (
                            <li key={name} className="mb-1">{name}</li>)))}
                    </ul>
                </div>
                <div className="mt-4">
                    <div className="font-semibold mb-1">Arrivent ce jour :</div>
                    <ul>
                        {arrivesToday(modal.day).length === 0 ? (
                            <li className="text-gray-400 italic">Personne</li>) : (arrivesToday(modal.day).map(name => (
                            <li key={name} className="mb-1">{name}</li>)))}
                    </ul>
                </div>
                <button
                    className="mt-4 bg-blue-700 text-white px-4 py-2 rounded-xl hover:bg-blue-800"
                    onClick={() => setModal({open: false})}
                >
                    Fermer
                </button>
            </>)}
        </Modal>

        {/* Modal : ajouter une présence */}

        <Modal open={addPresenceOpen} onClose={() => setAddPresenceOpen(false)}>
            <form onSubmit={handleAddPresence}>
                <div className="font-bold mb-2">
                    Ajouter une présence pour <span className="text-blue-700">{selectedMember?.pseudonym}</span>
                </div>
                <div className="mb-4">
                    <DateRange
                        locale={fr}
                        editableDateInputs={true}
                        onChange={item => setRange([item.selection])}
                        moveRangeOnFirstSelection={false}
                        ranges={range}
                        minDate={getSeasonStart()}
                        maxDate={getSeasonEnd()}
                        rangeColors={["#2563eb"]}         // bleu tailwind
                    />
                    <div className="text-xs mt-2">
                        {range[0].startDate && range[0].endDate && <>Séjour
                            du <b>{dayjs(range[0].startDate).format("DD/MM/YYYY")}</b> au <b>{dayjs(range[0].endDate).format("DD/MM/YYYY")}</b></>}
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        type="submit"
                        className="bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800"
                    >
                        Ajouter
                    </button>
                    <button
                        type="button"
                        className="bg-gray-200 px-4 py-2 rounded-xl"
                        onClick={() => setAddPresenceOpen(false)}
                    >
                        Annuler
                    </button>
                </div>
            </form>
        </Modal>
        <Modal open={commentModalOpen} onClose={() => setCommentModalOpen(false)}>
            <form onSubmit={async (e) => handleComment(e)}>
                <div className="font-bold mb-2">Commentaire pour le séjour du <span
                    className="text-blue-700">{editPresence ? dayjs(editPresence.arrival).format("DD/MM/YYYY") : ""}</span> au <span
                    className="text-blue-700">{editPresence ? dayjs(editPresence.departure).format("DD/MM/YYYY") : ""}</span>
                </div>
                <textarea
                    className="border w-full px-2 py-1 rounded-lg"
                    value={editCommentValue}
                    onChange={e => setEditCommentValue(e.target.value)}
                    placeholder="Ajouter un commentaire (facultatif)"
                    rows={3}
                />
                <div className="flex gap-2 mt-4">
                    <button type="submit"
                            className="bg-blue-700 text-white px-4 py-2 rounded-xl hover:bg-blue-800">Sauvegarder
                    </button>
                    <button type="button" className="bg-gray-200 px-4 py-2 rounded-xl"
                            onClick={() => setCommentModalOpen(false)}>Annuler
                    </button>
                </div>
            </form>
        </Modal>
    </div>);
}
